/**
 * Applies discount codes to the Shopify cart without redirecting to checkout.
 * Uses the Cart Ajax API and refreshes the cart drawer sections in place.
 */
(() => {
  'use strict';

  const pendingForms = new WeakSet();

  function getRoot() {
    return (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
  }

  function normalizeCode(value) {
    return String(value || '').trim();
  }

  function collectAppliedCodes(cart) {
    const codes = new Map();

    function addApplication(application) {
      const details = application?.discount_application || application;
      if (!details || details.type !== 'discount_code' || !details.title) return;
      const code = normalizeCode(details.title);
      if (code) codes.set(code.toLowerCase(), code);
    }

    (cart?.cart_level_discount_applications || []).forEach(addApplication);

    (cart?.items || []).forEach((item) => {
      (item.line_level_discount_allocations || []).forEach((allocation) => {
        addApplication(allocation.discount_application);
      });
    });

    (cart?.discount_codes || []).forEach((discount) => {
      if (!discount || discount.applicable === false) return;
      const code = normalizeCode(discount.code);
      if (code) codes.set(code.toLowerCase(), code);
    });

    return codes;
  }

  function getErrorElement(form) {
    return form?.querySelector('.cart-discount-form__error');
  }

  function hideError(form) {
    const error = getErrorElement(form);
    if (error) error.style.display = 'none';
  }

  function showError(form, message) {
    const error = getErrorElement(form);
    if (!error) return;
    if (message) error.textContent = message;
    error.style.display = 'block';
  }

  function setLoading(form, loading) {
    const button = form?.querySelector('button[type="submit"]');
    if (!button) return;
    button.disabled = loading;
    button.setAttribute('aria-disabled', loading ? 'true' : 'false');
    button.classList.toggle('loading', loading);
  }

  function getCartDrawer() {
    return document.querySelector('cart-drawer');
  }

  function getSections(cartDrawer) {
    if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
      return cartDrawer
        .getSectionsToRender()
        .map((section) => section.id)
        .filter(Boolean);
    }
    return ['cart-drawer', 'cart-icon-bubble'];
  }

  async function requestJson(url, options) {
    const response = await fetch(url, options);
    const data = await response.json();

    if (!response.ok || data?.status) {
      throw new Error(data?.description || data?.message || 'Discount code could not be applied.');
    }

    return data;
  }

  async function applyDiscount(form, code) {
    const root = getRoot();
    const cartDrawer = getCartDrawer();
    const currentCart = await requestJson(`${root}cart.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });

    const requestedCodes = collectAppliedCodes(currentCart);
    requestedCodes.set(code.toLowerCase(), code);

    const updatedCart = await requestJson(`${root}cart/update.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({
        discount: Array.from(requestedCodes.values()).join(','),
        sections: getSections(cartDrawer),
        sections_url: window.location.pathname,
      }),
    });

    const appliedCodes = collectAppliedCodes(updatedCart);
    if (!appliedCodes.has(code.toLowerCase())) {
      showError(form);
      return false;
    }

    if (cartDrawer && typeof cartDrawer.renderContents === 'function' && updatedCart.sections) {
      // The drawer is already open; refresh its contents without closing or reopening it.
      cartDrawer.renderContents(updatedCart, true);
    } else {
      window.location.reload();
    }

    document.dispatchEvent(
      new CustomEvent('cart:updated', {
        detail: { source: 'equipery-cart-discount', cartData: updatedCart },
      })
    );

    return true;
  }

  window.handleEquiperyDiscountForm = async function handleEquiperyDiscountForm(event) {
    event.preventDefault();
    event.stopPropagation();

    const form = event.currentTarget || event.target;
    if (!form || pendingForms.has(form)) return;

    const input = form.querySelector('[name="cart-discount-field"]');
    const code = normalizeCode(input?.value);

    hideError(form);
    if (!code) {
      showError(form);
      input?.focus();
      return;
    }

    pendingForms.add(form);
    setLoading(form, true);

    try {
      const applied = await applyDiscount(form, code);
      if (!applied) input?.focus();
    } catch (error) {
      console.error('[Equipery] Discount code failed:', error);
      showError(form, error.message);
    } finally {
      pendingForms.delete(form);
      setLoading(form, false);
    }
  };

  window.handleEquiperyDiscountFormChange = function handleEquiperyDiscountFormChange(event) {
    const form = event.target?.closest?.('.cart-discount-form');
    hideError(form);
  };
})();
